import React, { useEffect, useRef } from "react";
import { HashLink } from "react-router-hash-link";
import { ArrowRight, ArrowDown, Smartphone } from "lucide-react";
import { motion, useScroll, useSpring, useTransform, useReducedMotion } from "framer-motion";
import DotField from "./DotField";
import DeviceFrame from "../common/DeviceFrame";
import MagneticButton from "../common/MagneticButton";
import { PLAY_STORE_URL, SCREENS } from "../../content/product";

const EASE = [0.22, 1, 0.36, 1];

/**
 * Small, lerped mouse parallax. Writes transforms directly to the node so the
 * hero never re-renders on pointer movement.
 */
function useMouseParallax(ref, { maxShift = 10, maxTilt = 2.2 } = {}) {
  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const reduceMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isFinePointer =
      window.matchMedia && window.matchMedia("(pointer: fine)").matches;
    if (reduceMotion || !isFinePointer) return;

    let tx = 0;
    let ty = 0;
    let x = 0;
    let y = 0;
    let raf = null;
    let running = false;

    const step = () => {
      const dx = tx - x;
      const dy = ty - y;
      x += dx * 0.06;
      y += dy * 0.06;

      node.style.transform =
        `translate3d(${(x * maxShift).toFixed(2)}px, ${(y * maxShift).toFixed(2)}px, 0) ` +
        `rotateY(${(x * maxTilt).toFixed(2)}deg) rotateX(${(-y * maxTilt * 0.6).toFixed(2)}deg)`;

      if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
        running = false;
        raf = null;
        return;
      }
      raf = requestAnimationFrame(step);
    };

    const wake = () => {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(step);
    };

    const onMove = (e) => {
      tx = (e.clientX / window.innerWidth - 0.5) * 2;
      ty = (e.clientY / window.innerHeight - 0.5) * 2;
      wake();
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
    };
  }, [ref, maxShift, maxTilt]);
}

export default function HeroModern() {
  const sectionRef = useRef(null);
  const parallaxRef = useRef(null);
  const reduceMotion = useReducedMotion();

  useMouseParallax(parallaxRef);

  // Scroll progress across the hero, from "pinned at top" to "fully scrolled past".
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  // Softened so the transformation trails the scroll wheel instead of snapping.
  const p = useSpring(scrollYProgress, {
    stiffness: 55,
    damping: 22,
    restDelta: 0.0005,
  });

  // Copy recedes; the product takes over as the focus.
  const copyY = useTransform(p, [0, 1], [0, -96]);
  const copyOpacity = useTransform(p, [0, 0.42], [1, 0]);
  const ctaY = useTransform(p, [0, 1], [0, -64]);
  const ctaOpacity = useTransform(p, [0, 0.3], [1, 0]);
  const productY = useTransform(p, [0, 1], [0, -58]);
  const productScale = useTransform(p, [0, 1], [1, 1.07]);
  const sideOpacity = useTransform(p, [0, 0.55], [1, 0.25]);
  const scrollCueOpacity = useTransform(p, [0, 0.12], [1, 0]);

  const staticStyle = { y: 0, opacity: 1, scale: 1 };

  return (
    <section
      id="overview"
      ref={sectionRef}
      style={{
        position: "relative",
        paddingTop: "clamp(132px, 15vh, 176px)",
        paddingBottom: "clamp(64px, 9vh, 104px)",
        backgroundColor: "#ffffff",
        overflow: "hidden",
      }}
    >
      <DotField />

      <div className="container" style={{ position: "relative", zIndex: 2 }}>
        {/* ---------------------------------------------------------------- */}
        {/* Copy                                                             */}
        {/* ---------------------------------------------------------------- */}
        <motion.div
          className="row justify-content-center text-center"
          style={reduceMotion ? staticStyle : { y: copyY, opacity: copyOpacity }}
        >
          <div className="col-lg-10 col-xl-9">
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.02 }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "9px",
                marginBottom: "24px",
              }}
            >
              <img
                src="assets/1024.png"
                alt=""
                aria-hidden="true"
                style={{ width: "26px", height: "26px", borderRadius: "8px" }}
              />
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 600,
                  fontSize: "1.02rem",
                  color: "var(--text-strong)",
                  letterSpacing: "-0.02em",
                }}
              >
                EMIBOOK
              </span>
            </motion.div>

            <motion.h1
              className="eb-headline"
              initial={reduceMotion ? false : { opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, ease: EASE, delay: 0.14 }}
              style={{ maxWidth: "840px", margin: "0 auto" }}
            >
              Simplify EMI Management.
              <br />
              Empower Your Business.
            </motion.h1>

            <motion.p
              initial={reduceMotion ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.3 }}
              style={{
                marginTop: "22px",
                marginBottom: 0,
                fontSize: "1.05rem",
                color: "var(--text-muted)",
              }}
            >
              A complete EMI and loan management system — customers, schedules,
              payments and receipts in one app.
            </motion.p>
          </div>
        </motion.div>

        {/* ---------------------------------------------------------------- */}
        {/* Actions                                                          */}
        {/* ---------------------------------------------------------------- */}
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.42 }}
          style={reduceMotion ? staticStyle : { y: ctaY, opacity: ctaOpacity }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              marginTop: "34px",
              flexWrap: "wrap",
            }}
          >
            <MagneticButton>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="eb-btn eb-btn-primary"
              >
                <Smartphone size={16} /> Download for Android
              </a>
            </MagneticButton>

            <MagneticButton>
              <HashLink smooth to="#how-it-works" className="eb-btn eb-btn-secondary">
                Explore EMIBOOK <ArrowRight size={16} />
              </HashLink>
            </MagneticButton>
          </div>
        </motion.div>

        {/* ---------------------------------------------------------------- */}
        {/* Product                                                          */}
        {/* ---------------------------------------------------------------- */}
        <motion.div
          style={
            reduceMotion ? staticStyle : { y: productY, scale: productScale }
          }
        >
          <div
            ref={parallaxRef}
            style={{
              marginTop: "clamp(52px, 7vh, 82px)",
              perspective: "1400px",
              willChange: "transform",
            }}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
              }}
            >
              {/* Soft ground wash so the phones don't float on bare white */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  bottom: "-6%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "min(860px, 96%)",
                  height: "62%",
                  background:
                    "radial-gradient(ellipse at center, rgba(37, 99, 235, 0.07) 0%, rgba(79, 70, 229, 0.035) 40%, rgba(255,255,255,0) 72%)",
                  filter: "blur(6px)",
                  pointerEvents: "none",
                }}
              />

              <motion.div
                className="d-none d-lg-block"
                initial={reduceMotion ? false : { opacity: 0, y: 46, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1, ease: EASE, delay: 0.72 }}
                style={{
                  position: "relative",
                  zIndex: 1,
                  width: "182px",
                  marginRight: "-16px",
                  marginBottom: "44px",
                  opacity: reduceMotion ? 1 : sideOpacity,
                }}
              >
                <DeviceFrame
                  src={SCREENS.orders}
                  alt="EMIBOOK orders list"
                  style={{ maxWidth: "182px" }}
                />
              </motion.div>

              <motion.div
                initial={reduceMotion ? false : { opacity: 0, y: 54, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1.05, ease: EASE, delay: 0.58 }}
                style={{ position: "relative", zIndex: 3, width: "min(272px, 62vw)" }}
              >
                <DeviceFrame
                  src={SCREENS.dashboard}
                  alt="EMIBOOK dashboard showing orders and customers"
                  style={{ maxWidth: "272px" }}
                />
              </motion.div>

              <motion.div
                className="d-none d-lg-block"
                initial={reduceMotion ? false : { opacity: 0, y: 46, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1, ease: EASE, delay: 0.72 }}
                style={{
                  position: "relative",
                  zIndex: 1,
                  width: "182px",
                  marginLeft: "-16px",
                  marginBottom: "44px",
                  opacity: reduceMotion ? 1 : sideOpacity,
                }}
              >
                <DeviceFrame
                  src={SCREENS.payment}
                  alt="EMIBOOK payment entry screen"
                  style={{ maxWidth: "182px" }}
                />
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* ---------------------------------------------------------------- */}
        {/* Scroll cue                                                       */}
        {/* ---------------------------------------------------------------- */}
        <motion.div
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: EASE, delay: 1.2 }}
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "44px",
            opacity: reduceMotion ? 1 : scrollCueOpacity,
          }}
        >
          <HashLink
            smooth
            to="#how-it-works"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "7px",
              fontFamily: "var(--font-display)",
              fontSize: "0.8rem",
              fontWeight: 500,
              letterSpacing: "0.04em",
              color: "var(--text-light)",
              textDecoration: "none",
            }}
          >
            Scroll to see how it works <ArrowDown size={14} />
          </HashLink>
        </motion.div>
      </div>
    </section>
  );
}
